import { FieldInfo } from '@/components/FieldInfo';
import type { OrgFormApi } from '@/config/organizations/formTypes';
import { parseDate } from '@internationalized/date';
import { DatePicker } from '@nautilusbio/nb-design-system/DatePicker';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Switch } from '@nautilusbio/nb-design-system/Switch';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useStore } from '@tanstack/react-store';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import orgStyles from '../../styles.module.css';
import { gridProps, stackProps } from '../constants';

type SubscriptionSectionProps = { form: OrgFormApi };

function SubscriptionSectionComponent({ form }: SubscriptionSectionProps) {
  const { t } = useTranslation('orgs');
  const isActive = useStore(form.baseStore, s => s.values.subscription.active);

  return (
    <Stack {...stackProps} fullHeight>
      <Text as="h3" className={orgStyles.sectionHeading}>
        {t('create_org_section_subscription')}
      </Text>

      <Grid {...gridProps} alignItems="center" fullHeight placeContent="center">
        <form.Field name="subscription.active">
          {field => (
            <>
              <Switch
                checked={field.state.value}
                data-testid="input-subscription-active"
                id="subscription-is_active"
                label={t('create_org_subscription_active_label')}
                name="subscription-is_active"
                onChange={checked => field.handleChange(checked)}
                onBlur={field.handleBlur}
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>

        {isActive && (
          <form.Field name="subscription.expiration">
            {field => {
              const datePickerLabel = {
                children: t('create_org_subscription_expiration_label'),
                hasPlaceholder: !field.state.value,
                isFocused: false,
                hasValue: !!field.state.value,
                htmlFor: 'subscription-expires_at',
              };

              return (
                <>
                  <DatePicker
                    data-testid="input-subscription-expiration"
                    id="subscription-expires_at"
                    label={datePickerLabel}
                    name="subscription-expires_at"
                    onBlur={field.handleBlur}
                    onChange={date => field.handleChange(date ? date.toString() : '')}
                    value={(() => {
                      if (!field.state.value) return null;
                      try {
                        return parseDate(field.state.value);
                      } catch {
                        return null;
                      }
                    })()}
                  />

                  <FieldInfo field={field} />
                </>
              );
            }}
          </form.Field>
        )}
      </Grid>
    </Stack>
  );
}

export const SubscriptionSection = memo(SubscriptionSectionComponent);
